import { LISTING_STATUS } from '../constants/listingStatus';
import { findListingEntity, findListingVariantEntity } from './entityReadHelpers';
import getMasterIdsFromSelectionEntry from './getMasterIdsFromSelectionEntry';
import {
  createChannelSelectionContext,
  getEntitySelectionContext,
  MASTER_SELECTION_CONTEXT,
} from './selectionContext';

const getSelectedEntry = (selection) => (
  selection?.byKey?.[selection.keys?.[0]] || null
);

const listingVariantBelongsToProduct = (entry, productId, productsState) => {
  if (entry.masterProductId === productId) return true;

  const state = productsState || {};

  if (entry.baseProductId) {
    const parentListing = findListingEntity(state, {
      listingProductId: entry.baseProductId,
    });
    if (parentListing?.masterProductId === productId) return true;
  }

  if (!entry.masterVariantId) return false;

  const product = state.items?.find(({ _id }) => _id === productId);
  return Boolean(
    product?.variants?.some((variant) => variant._id === entry.masterVariantId),
  );
};

const belongsToProduct = (entry, productId, productsState) => {
  if (!entry || !productId) return false;

  if (entry.type === 'listingVariant') {
    return listingVariantBelongsToProduct(entry, productId, productsState);
  }

  const { productId: entryProductId } = getMasterIdsFromSelectionEntry(entry);
  return entryProductId === productId;
};

const canRemapSelection = (selection, productId, productsState) => {
  if (selection?.source !== 'tree' || selection.keys?.length !== 1) return false;
  return belongsToProduct(getSelectedEntry(selection), productId, productsState);
};

const resolveVariantId = (variantId, entry) => {
  if (variantId) return variantId;
  return getMasterIdsFromSelectionEntry(entry).variantId;
};

const normalizeListingStatus = (tab) => (
  String(tab || '').toUpperCase() === LISTING_STATUS.PUBLISHED
    ? LISTING_STATUS.PUBLISHED
    : LISTING_STATUS.DRAFT
);

const recordStateFromStatus = (listingStatus) => (
  listingStatus === LISTING_STATUS.DRAFT ? 'draft' : 'published'
);

const clampLayerTab = (requestedTab, listing) => {
  if (!listing) return requestedTab;

  const hasStaging = listing.listingStatus === LISTING_STATUS.DRAFT;
  const hasLive = Boolean(listing.live || listing.listingProduct?.live);

  if (requestedTab === LISTING_STATUS.PUBLISHED && hasLive) {
    return LISTING_STATUS.PUBLISHED;
  }
  if (requestedTab === LISTING_STATUS.DRAFT && hasStaging) {
    return LISTING_STATUS.DRAFT;
  }
  if (hasStaging) return LISTING_STATUS.DRAFT;
  if (hasLive) return LISTING_STATUS.PUBLISHED;
  return requestedTab;
};

const resolveLayerTab = ({ tab, productsState, productId, channelId, listing }) => {
  if (tab) return tab;
  const storedTab = productsState?.ui?.activeTabByProductChannel?.[productId]?.[channelId];
  if (storedTab) return storedTab;
  if (listing?.listingStatus) return listing.listingStatus;
  return LISTING_STATUS.DRAFT;
};

const buildMasterItem = (productId, variantId) => {
  if (variantId) {
    return {
      type: 'masterVariant',
      _id: variantId,
      baseProductId: productId,
    };
  }

  return {
    type: 'master',
    _id: productId,
  };
};

const channelIdsFromEntity = (entity, channelId) => {
  const resolvedChannelId = entity.channelId || entity.salesChannelId || channelId;
  const salesChannelId = entity.salesChannelId || entity.channelId || channelId;
  return {
    ...(resolvedChannelId ? { channelId: resolvedChannelId } : {}),
    ...(salesChannelId ? { salesChannelId } : {}),
  };
};

const toListingSelectionItem = (listing, productId, listingStatus, channelId) => ({
  type: 'listing',
  _id: listing._id,
  masterProductId: listing.masterProductId || productId,
  listingStatus,
  ...channelIdsFromEntity(listing, channelId),
  ...(listing.title != null ? { title: listing.title } : {}),
});

const toListingVariantSelectionItem = (listingVariant, productId, listingStatus, channelId) => ({
  type: 'listingVariant',
  _id: listingVariant._id,
  masterVariantId: listingVariant.masterVariantId,
  baseProductId: listingVariant.baseProductId,
  masterProductId: listingVariant.masterProductId || productId,
  listingStatus,
  ...channelIdsFromEntity(listingVariant, channelId),
  ...(listingVariant.title != null ? { title: listingVariant.title } : {}),
});

export default function resolveTreeLayerSelectionItem({
  selection,
  productId,
  variantId,
  channelId,
  tab,
  productsState,
} = {}) {
  const state = productsState || {};

  if (!productId || !channelId || !canRemapSelection(selection, productId, state)) {
    return null;
  }

  const entry = getSelectedEntry(selection);
  const resolvedVariantId = resolveVariantId(variantId, entry);

  if (channelId === 'master') {
    const item = buildMasterItem(productId, resolvedVariantId);
    return {
      item,
      context: getEntitySelectionContext(item) || MASTER_SELECTION_CONTEXT,
    };
  }

  const listing = findListingEntity(state, { masterProductId: productId, channelId });
  const listingStatus = clampLayerTab(normalizeListingStatus(resolveLayerTab({
    tab,
    productsState: state,
    productId,
    channelId,
    listing,
  })), listing);
  const channelContext = createChannelSelectionContext(
    channelId,
    recordStateFromStatus(listingStatus),
  );

  if (resolvedVariantId) {
    const listingVariant = findListingVariantEntity(state, resolvedVariantId, channelId);
    if (listingVariant) {
      const item = toListingVariantSelectionItem(
        listingVariant,
        productId,
        listingStatus,
        channelId,
      );
      return {
        item,
        context: getEntitySelectionContext(item) || channelContext,
      };
    }

    return {
      item: buildMasterItem(productId, resolvedVariantId),
      context: channelContext,
    };
  }

  if (listing) {
    const item = toListingSelectionItem(listing, productId, listingStatus, channelId);
    return {
      item,
      context: getEntitySelectionContext(item) || channelContext,
    };
  }

  return {
    item: buildMasterItem(productId, resolvedVariantId),
    context: channelContext,
  };
}
